/* --- FILE: src\components\EventCountDownTimer\EventCountDownTimer.js --- */
import React, { useEffect, useState } from "react";
import { CountdownCircleTimer } from "react-countdown-circle-timer";
import "./EventCountDownTimer.css";

const minuteSeconds = 60;
const hourSeconds = 3600;
const daySeconds = 86400;

const timerProps = {
  isPlaying: true,
  strokeWidth: 4, // Restored thickness
  trailColor: "#e5e5e5"
};

const renderTime = (dimension, time) => {
  return (
    <div className="time-wrapper">
      <div className="time">{time}</div>
      <div className="dimension">{dimension}</div>
    </div>
  );
};

const getTimeSeconds = (time) => (minuteSeconds - time) | 0;
const getTimeMinutes = (time) => ((time % hourSeconds) / minuteSeconds) | 0;
const getTimeHours = (time) => ((time % daySeconds) / hourSeconds) | 0;
const getTimeDays = (time) => (time / daySeconds) | 0;

export function EventCountDownTimer({ eventcountdown }) {
  
  const [size, setSize] = useState(70);
  // State to hold the calculated times based on your old file's logic
  const [timerData, setTimerData] = useState({
      remainingTime: null,
      daysDuration: null
  });

  // 1. Responsive Size Logic (Restored from your logic but using Hooks)
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 767) {
        setSize(55); // Smaller for mobile
      } else {
        setSize(70); // Desktop size
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Run once on mount

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 2. Calculation Logic (Restored from your old file)
  useEffect(() => {
    if (eventcountdown) {
        let endTime;

        // Handle Date String vs Timestamp
        if (typeof eventcountdown === 'string') {
             // Convert string (e.g., "2023-12-25 10:00:00") to UNIX timestamp in seconds
             const formattedDate = eventcountdown.replace(/-/g, "/"); 
             endTime = new Date(formattedDate).getTime() / 1000;
        } else {
             // Already a timestamp
             endTime = eventcountdown;
        }

        const startTime = Date.now() / 1000;
        const remaining = endTime - startTime;

        // Logic from your old file to calculate Day Circle duration
        const days = Math.ceil(remaining / daySeconds);
        const dDuration = days * daySeconds;

        if (remaining > 0) {
            setTimerData({
                remainingTime: remaining,
                daysDuration: dDuration
            });
        } else {
            setTimerData({ remainingTime: 0, daysDuration: 0 });
        }
    }
  }, [eventcountdown]);

  // Loading State
  if (timerData.remainingTime === null) return null;

  // Event Ended State
  if (timerData.remainingTime <= 0) {
      return (
        <div className="Timer w-100 d-flex justify-content-center">
            <span style={{fontSize:'14px', fontWeight:'bold', color:'#135592'}}>Event In Progress</span>
        </div>
      );
  }

  return (
    <div className="Timer" key={timerData.remainingTime}> 
      {/* Key is crucial: forces re-render when data is ready */}
      
      <CountdownCircleTimer
        {...timerProps}
        colors="#135592"
        size={size}
        duration={timerData.daysDuration} 
        initialRemainingTime={timerData.remainingTime}
      >
        {({ elapsedTime, color }) => (
          <span style={{ color }}>
            {renderTime("Days", getTimeDays(timerData.daysDuration - elapsedTime))}
          </span>
        )}
      </CountdownCircleTimer>

      <CountdownCircleTimer
        {...timerProps}
        colors="#f5a425"
        size={size}
        duration={daySeconds}
        initialRemainingTime={timerData.remainingTime % daySeconds}
        onComplete={(totalElapsedTime) => ({
          shouldRepeat: timerData.remainingTime - totalElapsedTime > hourSeconds
        })}
      >
        {({ elapsedTime, color }) => (
          <span style={{ color }}>
            {renderTime("Hours", getTimeHours(daySeconds - elapsedTime))}
          </span>
        )}
      </CountdownCircleTimer>

      <CountdownCircleTimer
        {...timerProps}
        colors="#135592"
        size={size}
        duration={hourSeconds}
        initialRemainingTime={timerData.remainingTime % hourSeconds}
        onComplete={(totalElapsedTime) => ({
          shouldRepeat: timerData.remainingTime - totalElapsedTime > minuteSeconds
        })}
      >
        {({ elapsedTime, color }) => (
          <span style={{ color }}>
            {renderTime("Mins", getTimeMinutes(hourSeconds - elapsedTime))}
          </span>
        )}
      </CountdownCircleTimer>

      <CountdownCircleTimer
        {...timerProps}
        colors="#dc3545"
        size={size}
        duration={minuteSeconds}
        initialRemainingTime={timerData.remainingTime % minuteSeconds}
        onComplete={(totalElapsedTime) => ({
          shouldRepeat: timerData.remainingTime - totalElapsedTime > 0
        })}
      >
        {({ elapsedTime, color }) => (
          <span style={{ color }}>
            {renderTime("Secs", getTimeSeconds(elapsedTime))}
          </span>
        )}
      </CountdownCircleTimer>
    </div>
  );
}